import { QueryResultCallback } from "../../models/Callback/Callback";
import { Individual } from "../../models/Contact/Individual/Individual";
import { IndividualDependentAttrs } from "../../models/Contact/Individual/IndividualDependent";
import { ContactFileAttrs } from "../../models/Contact/ContactFile";
import { ContactNoteAttrs } from "../../models/Contact/ContactNote";
import { ContactServiceAttrs } from "../../models/Contact/ContactService";
import { TIndividualAttrs, TContactDeletableProps } from "../../models/Contact/Types";
import { IndividualStoreActions } from "../../redux/services/contacts/actions";
import { AzureBlobStorage } from "../../redux/utils/AzureBlobStorage";
import { APIManager } from "../APIManager";
import { formatUrl } from "../../helpers/misc";

export class IndividualManager {
    static newContactObj(data: TIndividualAttrs) {
        return new Individual(data);
    }

    getContact(individualId: string) {
        return IndividualStoreActions.getIndividuals().find((t) => t.id === individualId);
    }

    async apiFetchAllContacts(cb: QueryResultCallback<Individual[]>, includeDeleted = false) {
        try {
            IndividualStoreActions.loadingIndividuals(true);
            const resp = await APIManager.get(
                formatUrl(APIManager.CONTACT.INDIVIDUAL.LIST_API, { includeDeleted: includeDeleted ? "1" : undefined }),
            );
            const individuals: Individual[] = resp.data.map((t: TIndividualAttrs) => this.updateContactAttrOrAddNew(t, false));
            if (!includeDeleted) {
                IndividualStoreActions.saveIndividuals(individuals);
            }
            IndividualStoreActions.setIndividualLoaded(true);
            cb(null, individuals);
            return individuals;
        } catch (err) {
            cb(`${err}`);
        } finally {
            IndividualStoreActions.loadingIndividuals(false);
        }
    }

    async apiFetchDeletedContacts(cb: QueryResultCallback<Individual[]>) {
        try {
            IndividualStoreActions.loadingIndividuals(true);
            const resp = await APIManager.get(formatUrl(APIManager.CONTACT.INDIVIDUAL.LIST_API, { deletedOnly: "1" }));
            const individuals: Individual[] = resp.data.map((t: TIndividualAttrs) => this.updateContactAttrOrAddNew(t, false));
            IndividualStoreActions.setIndividualLoaded(true);
            cb(null, individuals);

            return individuals;
        } catch (err) {
            cb(`${err}`);
        } finally {
            IndividualStoreActions.loadingIndividuals(false);
        }
    }

    async apiFetchContactAttrs(individualId: string, cb: QueryResultCallback<Individual>) {
        try {
            const resp = await APIManager.get(APIManager.CONTACT.INDIVIDUAL.ITEM_API(individualId));
            const individual = this.updateContactAttrOrAddNew(resp.data);
            cb(null, individual);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiFetchContactNotes(individualId: string, cb: QueryResultCallback<ContactNoteAttrs[]>) {
        try {
            const resp = await APIManager.get(APIManager.CONTACT.INDIVIDUAL.NOTES_API(individualId));
            const individual = this.getContact(individualId);
            if (individual) {
                individual.updateNotes(resp.data);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiFetchContactServices(individualId: string, cb: QueryResultCallback<ContactServiceAttrs[]>) {
        try {
            const resp = await APIManager.get(APIManager.CONTACT.INDIVIDUAL.SERVICES_API(individualId));
            const individual = this.getContact(individualId);
            if (individual) {
                individual.updateServices(resp.data);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiFetchContactFiles(individualId: string, cb: QueryResultCallback<ContactNoteAttrs[]>) {
        try {
            const resp = await APIManager.get(APIManager.CONTACT.INDIVIDUAL.FILES_API(individualId));
            const individual = this.getContact(individualId);
            if (individual) {
                individual.updateFiles(resp.data);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiFetchIndividualDependents(individualId: string, cb: QueryResultCallback<IndividualDependentAttrs[]>) {
        try {
            const resp = await APIManager.get(APIManager.CONTACT.INDIVIDUAL.DEPENDENTS_API(individualId));
            const individual = this.getContact(individualId);
            if (individual) {
                individual.updateDependents(resp.data);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async rehydrateContact(individualId: string, cb: QueryResultCallback<Individual[]>, rehydratePartner = true) {
        const individual = this.getContact(individualId);
        const errors: string[] = [];
        const callback = (err: string | null) => {
            if (err) errors.push(err);
        };
        if (individual) {
            await this.apiFetchContactAttrs(individualId, callback);
            await this.apiFetchContactNotes(individualId, callback);
            await this.apiFetchContactFiles(individualId, callback);
            await this.apiFetchContactServices(individualId, callback);
            await this.apiFetchIndividualDependents(individualId, callback);
            if (rehydratePartner && individual.data.partner) await this.rehydrateContact(individual.data.partner.id, callback, false);
        }
        cb(errors.length ? errors.join("\n") : null);
    }

    async apiSaveContactAuto(indvData: Partial<TIndividualAttrs>, templates: string[], cb: QueryResultCallback<Individual>) {
        try {
            const resp = await APIManager.post(APIManager.CONTACT.INDIVIDUAL.AUTO_REGISTER_API, { contactData: indvData, templates });
            const individual: Individual = IndividualManager.newContactObj(resp.data);
            IndividualStoreActions.addOrUpdateToIndividuals(individual);
            cb(null, individual);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiSaveContact(indvData: Partial<TIndividualAttrs>, cb: QueryResultCallback<Individual>) {
        try {
            const resp = await APIManager.post(APIManager.CONTACT.INDIVIDUAL.LIST_API, indvData);
            const individual: Individual = IndividualManager.newContactObj(resp.data);
            IndividualStoreActions.addOrUpdateToIndividuals(individual);
            cb(null, individual);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiSaveContactNote(
        individualId: string,
        noteData: { title: string; note: string; type: string },
        cb: QueryResultCallback<ContactNoteAttrs[]>,
    ) {
        try {
            const resp = await APIManager.post(APIManager.CONTACT.INDIVIDUAL.NOTES_API(individualId), noteData);
            const individual = this.getContact(individualId);
            if (individual) {
                individual.addOrUpdateNote(resp.data);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiSaveIndividualDependent(
        individualId: string,
        depData: { title: string; firstName: string; lastName: string; relation: string },
        cb: QueryResultCallback<IndividualDependentAttrs[]>,
    ) {
        try {
            const resp = await APIManager.post(APIManager.CONTACT.INDIVIDUAL.DEPENDENTS_API(individualId), depData);
            const individual = this.getContact(individualId);
            if (individual) {
                individual.addOrUpdateDependent(resp.data);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiSaveContactService(
        individualId: string,
        serviceData: { serviceId: string; serviceDate: string },
        cb: QueryResultCallback<ContactServiceAttrs[]>,
    ) {
        try {
            const resp = await APIManager.post(APIManager.CONTACT.INDIVIDUAL.SERVICES_API(individualId), serviceData);
            const individual = this.getContact(individualId);
            if (individual) {
                individual.addOrUpdateService(resp.data);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiSaveContactFile(individualId: string, contactNames: string, file: File, source: string, cb: QueryResultCallback<ContactFileAttrs>) {
        const azure = AzureBlobStorage.newInstance();
        azure.uploadToBlobStorage(file, contactNames, async (err, respData) => {
            if (err) cb(`${err}`);
            else {
                const fileData = {
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    source,
                    blobName: respData!.blobName,
                    blobUrl: respData!.blobUrl,
                };
                try {
                    const resp = await APIManager.post(APIManager.CONTACT.INDIVIDUAL.FILES_API(individualId), fileData);
                    const individual = this.getContact(individualId);
                    if (individual) {
                        individual.addOrUpdateFile(resp.data);
                    }
                    cb(null, resp.data);
                } catch (err) {
                    azure.deleteBlobFromStorage(respData!.blobName);
                    cb(`${err}`);
                }
            }
        });
    }

    updateContactAttrOrAddNew(contactAttr: TIndividualAttrs, saveToStore = true) {
        let individual = this.getContact(contactAttr._id);
        if (individual) {
            individual.updateIndividualAttrs(contactAttr);
        } else {
            individual = IndividualManager.newContactObj(contactAttr);
        }
        if (saveToStore) IndividualStoreActions.addOrUpdateToIndividuals(individual);
        return individual;
    }

    async apiUpdateContactNote(
        individualId: string,
        noteId: string,
        noteData: { title?: string; note?: string; type?: string },
        cb: QueryResultCallback<ContactNoteAttrs[]>,
    ) {
        try {
            const resp = await APIManager.put(APIManager.CONTACT.INDIVIDUAL.NOTE_ITEM_API(individualId, noteId), noteData);
            const individual = this.getContact(individualId);
            if (individual) {
                console.log("NOTE UPDATED ", resp.data);
                individual.addOrUpdateNote(resp.data);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiUpdateIndividualDependent(individualId: string, dependentId: string, depData: any, cb: QueryResultCallback<IndividualDependentAttrs[]>) {
        try {
            const resp = await APIManager.put(APIManager.CONTACT.INDIVIDUAL.DEPENDENT_ITEM_API(individualId, dependentId), depData);
            const individual = this.getContact(individualId);
            if (individual) {
                console.log("DEPENDENT UPDATED ", resp.data);
                individual.addOrUpdateDependent(resp.data);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiUpdateContact(individualId: string, contactData: Partial<TIndividualAttrs>, cb: QueryResultCallback<Individual>) {
        try {
            const resp = await APIManager.put(APIManager.CONTACT.INDIVIDUAL.ITEM_API(individualId), contactData);
            const individual = this.updateContactAttrOrAddNew(resp.data);
            cb(null, individual);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiDeleteContactNote(individualId: string, noteId: string, cb: QueryResultCallback<ContactNoteAttrs[]>) {
        try {
            const note = await APIManager.get(APIManager.CONTACT.INDIVIDUAL.NOTE_ITEM_API(individualId, noteId));
            const noteAttrs: ContactNoteAttrs = note.data;
            for (const attachment of noteAttrs.attachments) {
                await new Promise((resolve, reject) => {
                    this.apiDeleteContactFile(individualId, attachment._id, (err, d) => {
                        if (err) reject(`${err}`);
                        else resolve(d!);
                    });
                });
            }
            const resp = await APIManager.delete(APIManager.CONTACT.INDIVIDUAL.NOTE_ITEM_API(individualId, noteId));
            const individual = this.getContact(individualId);
            if (individual) {
                individual.removeNoteWithId(noteId);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiDeleteIndividualDependent(individualId: string, dependentId: string, cb: QueryResultCallback<IndividualDependentAttrs[]>) {
        try {
            const resp = await APIManager.delete(APIManager.CONTACT.INDIVIDUAL.DEPENDENT_ITEM_API(individualId, dependentId));
            const individual = this.getContact(individualId);
            if (individual) {
                individual.removeDependentWithId(dependentId);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiDeleteContactService(individualId: string, serviceId: string, cb: QueryResultCallback<ContactServiceAttrs[]>) {
        try {
            const resp = await APIManager.delete(APIManager.CONTACT.INDIVIDUAL.SERVICE_ITEM_API(individualId, serviceId));
            const individual = this.getContact(individualId);
            if (individual) {
                individual.removeServiceWithId(serviceId);
            }
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiDeleteContactFile(individualId: string, fileId: string, cb: QueryResultCallback<ContactFileAttrs>) {
        const azure = AzureBlobStorage.newInstance();
        try {
            const fileItem = await APIManager.get(APIManager.CONTACT.INDIVIDUAL.FILE_ITEM_API(individualId, fileId));
            const { blobName } = fileItem.data;
            azure.deleteBlobFromStorage(blobName, async (err) => {
                if (err) cb(`${err}`);
                else {
                    try {
                        const resp = await APIManager.delete(APIManager.CONTACT.INDIVIDUAL.FILE_ITEM_API(individualId, fileId));
                        const individual = this.getContact(individualId);
                        if (individual) {
                            individual.removeFileWithId(fileId);
                        }
                        cb(null, resp.data);
                    } catch (err) {
                        azure.undeleteBlobFromStorage(blobName);
                        cb(`${err}`);
                    }
                }
            });
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiDeleteContact(individualId: string, cb: QueryResultCallback) {
        try {
            const resp = await APIManager.delete(APIManager.CONTACT.INDIVIDUAL.ITEM_API(individualId));
            this.removeContact(individualId);
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    removeContact(individualId: string) {
        const individual = this.getContact(individualId);
        if (individual) {
            individual.remove();
            IndividualStoreActions.removeIndividual(individual);
        } else console.log("CONTACT WITH ID ", individualId, " NOT FOUND");
    }

    async apiCheckContactDeletable(individualId: string, cb: QueryResultCallback<TContactDeletableProps>) {
        try {
            const resp = await APIManager.get(APIManager.CONTACT.INDIVIDUAL.CHECK_CONTACT_DELETABLE_API(individualId));
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }

    async apiSendEmail(individualId: string, emailData: { email: string; subject: string; body: string }, cb: QueryResultCallback<any>) {
        try {
            const resp = await APIManager.post(APIManager.CONTACT.INDIVIDUAL.SEND_EMAIL_API(individualId), emailData);
            cb(null, resp.data);
        } catch (err) {
            cb(`${err}`);
        }
    }
}
